import { useEffect, useState, FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  CreditTransaction,
  FitReturn,
  Order,
  OrderItem,
  OrderStatus,
  Profile
} from '@lr/shared';
import { apiFetch } from '../../lib/api';
import { StatusBadge } from './AdminDashboard';

interface OrderDetail {
  order: Order & { order_items: OrderItem[] };
  user: Profile | null;
  fit_returns: FitReturn[];
  credit_transactions: CreditTransaction[];
}

const STATUS_OPTIONS: { value: OrderStatus; label: string }[] = [
  { value: 'pending_payment', label: 'Pending payment' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'preparing', label: 'Preparing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'returning', label: 'Return requested' },
  { value: 'return_label_sent', label: 'Return label sent' },
  { value: 'returned', label: 'Returned' },
  { value: 'cancelled', label: 'Cancelled' }
];

export function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editStatus, setEditStatus] = useState<OrderStatus | ''>('');
  const [tracking, setTracking] = useState('');
  const [notes, setNotes] = useState('');

  async function deleteOrder() {
    if (!data) return;
    const num = data.order.order_number;
    if (
      !window.confirm(
        `Delete order ${num}? This wipes the order and its line items, and releases the items back to available. This can't be undone.`
      )
    )
      return;
    try {
      await apiFetch(`/api/admin/orders/${id}`, { method: 'DELETE' });
      navigate('/admin/orders');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  function load() {
    setLoading(true);
    setError(null);
    apiFetch<OrderDetail>(`/api/admin/orders/${id}`)
      .then((d) => {
        setData(d);
        setEditStatus(d.order.status);
        setTracking(d.order.tracking_number ?? '');
        setNotes(d.order.notes ?? '');
      })
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'Failed to load order')
      )
      .finally(() => setLoading(false));
  }
  useEffect(load, [id]);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!data) return;
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {};
      if (editStatus && editStatus !== data.order.status) body.status = editStatus;
      if ((tracking || null) !== (data.order.tracking_number ?? null)) {
        body.tracking_number = tracking || null;
      }
      if ((notes || null) !== (data.order.notes ?? null)) {
        body.notes = notes || null;
      }
      if (Object.keys(body).length === 0) {
        setSaving(false);
        return;
      }
      await apiFetch(`/api/admin/orders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body)
      });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={{ color: 'var(--a-ink-soft)', padding: 16 }}>Loading…</div>;
  }
  if (error && !data) {
    return <div className="a-alert a-alert-error">{error}</div>;
  }
  if (!data) return null;

  const { order, user, fit_returns, credit_transactions } = data;
  const items = order.order_items ?? [];
  const isBundlePurchase = !!order.bundle_purchased_id && items.length === 0;

  // Shipping address — pulled from shipping_snapshot on the order. Falls
  // back to the profile address only if the snapshot is empty (rare —
  // place_order always writes a snapshot).
  const ship =
    (order.shipping_snapshot as Record<string, string> | null) ?? null;
  const addr = {
    line1: ship?.line1 ?? user?.shipping_line1 ?? '',
    line2: ship?.line2 ?? user?.shipping_line2 ?? '',
    city: ship?.city ?? user?.shipping_city ?? '',
    postcode: ship?.postcode ?? user?.shipping_postcode ?? '',
    country: ship?.country ?? user?.shipping_country ?? ''
  };

  const subTotalPence =
    order.bundle_amount_pence +
    order.delivery_fee_pence -
    order.discount_amount_pence;

  return (
    <>
      <div className="admin-breadcrumb">
        <Link to="/admin/orders">Orders</Link>
        <span className="sep">/</span>
        <span>{order.order_number}</span>
      </div>

      <div className="admin-page-head">
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {order.order_number}
            <StatusBadge status={order.status} />
          </h1>
          <div className="sub">
            Placed {new Date(order.created_at).toLocaleString('en-GB')}
            {order.return_due_date && (
              <>
                {' '}· Return due{' '}
                <strong>
                  {new Date(order.return_due_date).toLocaleDateString('en-GB')}
                </strong>
              </>
            )}
          </div>
        </div>
        <div className="actions" style={{ flexWrap: 'wrap' }}>
          <button
            className="btn-secondary"
            onClick={() => navigate('/admin/orders')}
          >
            ← Back to orders
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => void deleteOrder()}
            style={{ color: 'var(--a-danger, #b3261e)' }}
          >
            Delete order
          </button>
        </div>
      </div>

      {error && <div className="a-alert a-alert-error">{error}</div>}

      {/* QUICK ACTIONS — visible at the top of every order so admin can
          push the order through its lifecycle without scrolling to the
          status dropdown. ID nudge appears whenever ID isn't approved. */}
      <QuickActions
        order={order}
        user={user}
        onUpdated={load}
      />

      <div className="order-detail-grid">
        {/* Two-col on desktop, single-col on mobile (admin.css). */}
        {/* LEFT — items, address, fit returns */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* ITEMS */}
          <div className="a-card">
            <div className="a-card-head">
              <h2>
                {isBundlePurchase
                  ? 'Credit bundle'
                  : `Items (${items.length})`}
              </h2>
            </div>
            {isBundlePurchase ? (
              <div className="a-card-body">
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Bundle purchase</span>
                  <strong>£{(order.bundle_amount_pence / 100).toFixed(2)}</strong>
                </div>
                <div className="help" style={{ marginTop: 6 }}>
                  This order is a credit-bundle purchase. No physical items are
                  shipped — credits were added to the customer&apos;s account.
                </div>
              </div>
            ) : items.length === 0 ? (
              <div className="a-empty">
                <div className="h">No items on this order</div>
              </div>
            ) : (
              <div className="a-table-wrap">
                <table className="a-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Slug</th>
                      <th>Brand</th>
                      <th style={{ width: 90 }}>Credits</th>
                      <th style={{ width: 120 }}>Damage fee</th>
                      <th style={{ width: 140 }}>Returned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((oi) => (
                      <tr key={oi.id}>
                        <td>
                          <Link
                            to={`/admin/items/${oi.item_id}`}
                            style={{ fontWeight: 600 }}
                          >
                            {oi.item_name_snapshot}
                          </Link>
                        </td>
                        <td
                          className="id"
                          style={{ fontSize: 12, color: 'var(--a-ink-soft)' }}
                        >
                          {(oi as unknown as { item?: { slug?: string } | null }).item
                            ?.slug ?? '—'}
                        </td>
                        <td>{oi.brand_name_snapshot}</td>
                        <td className="num">{oi.credit_price_snapshot}</td>
                        <td className="num">
                          £{(oi.damage_fee_snapshot / 100).toFixed(2)}
                          {oi.damage_applied && (
                            <div
                              style={{
                                fontSize: 10,
                                color: 'var(--a-danger)',
                                marginTop: 2
                              }}
                            >
                              Charged £{(oi.damage_fee_charged_pence / 100).toFixed(2)}
                            </div>
                          )}
                        </td>
                        <td>
                          {oi.returned_at
                            ? new Date(oi.returned_at).toLocaleDateString('en-GB')
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ADDRESS */}
          {!isBundlePurchase && (
            <div className="a-card">
              <div className="a-card-head">
                <h2>Shipping address</h2>
              </div>
              <div className="a-card-body">
                {/* Customer name sits in its own block above the address
                    so the warehouse picker can see at a glance who the
                    parcel is going to. */}
                {user && (
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 700,
                      marginBottom: 10
                    }}
                  >
                    {[user.first_name, user.last_name].filter(Boolean).join(' ') ||
                      user.email}
                  </div>
                )}
                {addr.line1 ? (
                  <address
                    style={{
                      fontStyle: 'normal',
                      lineHeight: 1.6,
                      fontSize: 14
                    }}
                  >
                    <div>{addr.line1}</div>
                    {addr.line2 && <div>{addr.line2}</div>}
                    <div>
                      {addr.city}
                      {addr.city && addr.postcode ? ', ' : ''}
                      {addr.postcode}
                    </div>
                    {addr.country && <div>{addr.country}</div>}
                    {user?.phone && (
                      <div style={{ marginTop: 8, color: 'var(--a-ink-soft)' }}>
                        ☏ {user.phone}
                      </div>
                    )}
                  </address>
                ) : (
                  <div className="a-empty">
                    <div className="h">No shipping address on this order</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* FIT RETURNS */}
          {fit_returns.length > 0 && (
            <div className="a-card">
              <div className="a-card-head">
                <h2>Fit return requests ({fit_returns.length})</h2>
              </div>
              <div className="a-table-wrap">
                <table className="a-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Reason</th>
                      <th style={{ width: 100 }}>Credits</th>
                      <th style={{ width: 130 }}>Requested</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fit_returns.map((f) => (
                      <tr key={f.id}>
                        <td>
                          <span
                            className={`badge ${
                              f.status === 'approved'
                                ? 'badge-good'
                                : f.status === 'rejected'
                                  ? 'badge-danger'
                                  : 'badge-warn'
                            }`}
                          >
                            {f.status}
                          </span>
                        </td>
                        <td style={{ fontSize: 13 }}>{f.reason ?? '—'}</td>
                        <td className="num">{f.credits_reissued ?? 0}</td>
                        <td style={{ fontSize: 12 }}>
                          {new Date(f.requested_at).toLocaleDateString('en-GB')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CREDIT TRANSACTIONS */}
          {credit_transactions.length > 0 && (
            <div className="a-card">
              <div className="a-card-head">
                <h2>Credit movements ({credit_transactions.length})</h2>
              </div>
              <div className="a-table-wrap">
                <table className="a-table">
                  <thead>
                    <tr>
                      <th>Reason</th>
                      <th style={{ width: 90 }}>Amount</th>
                      <th>Note</th>
                      <th style={{ width: 130 }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {credit_transactions.map((t) => (
                      <tr key={t.id}>
                        <td style={{ fontSize: 13 }}>
                          {t.reason.replace(/_/g, ' ')}
                        </td>
                        <td
                          className="num"
                          style={{
                            color:
                              t.amount > 0 ? 'var(--a-good)' : 'var(--a-ink)',
                            fontWeight: 600
                          }}
                        >
                          {t.amount > 0 ? '+' : ''}
                          {t.amount}
                        </td>
                        <td
                          style={{
                            fontSize: 12,
                            color: 'var(--a-ink-soft)'
                          }}
                        >
                          {t.notes ?? '—'}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {new Date(t.created_at).toLocaleDateString('en-GB')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — customer + totals + status editor */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* CUSTOMER */}
          {user && (
            <div className="a-card">
              <div className="a-card-head">
                <h2>Customer</h2>
              </div>
              <div className="a-card-body" style={{ fontSize: 14 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {[user.first_name, user.last_name].filter(Boolean).join(' ') ||
                    user.email}
                </div>
                <div className="id" style={{ marginBottom: 10 }}>
                  {user.email}
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12,
                    marginBottom: 4
                  }}
                >
                  <span style={{ color: 'var(--a-ink-soft)' }}>ID status</span>
                  <span
                    className={`badge ${
                      user.id_status === 'approved'
                        ? 'badge-good'
                        : user.id_status === 'pending'
                          ? 'badge-warn'
                          : user.id_status === 'rejected'
                            ? 'badge-danger'
                            : 'badge-neutral'
                    }`}
                  >
                    {(user.id_status ?? 'not_submitted').replace('_', ' ')}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12,
                    marginBottom: 4
                  }}
                >
                  <span style={{ color: 'var(--a-ink-soft)' }}>Credits</span>
                  <strong>{user.credit_balance ?? 0}</strong>
                </div>
                <Link
                  to={`/admin/customers/${user.id}`}
                  className="btn-secondary"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    marginTop: 14
                  }}
                >
                  View customer →
                </Link>
              </div>
            </div>
          )}

          {/* TOTALS */}
          <div className="a-card">
            <div className="a-card-head">
              <h2>Totals</h2>
            </div>
            <div
              className="a-card-body"
              style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}
            >
              <Row label="Credits used" value={`${order.credits_used}`} />
              {order.bundle_amount_pence > 0 && (
                <Row
                  label="Bundle"
                  value={`£${(order.bundle_amount_pence / 100).toFixed(2)}`}
                />
              )}
              <Row
                label="Delivery"
                value={
                  order.delivery_fee_pence > 0
                    ? `£${(order.delivery_fee_pence / 100).toFixed(2)}`
                    : 'FREE'
                }
              />
              {order.discount_amount_pence > 0 && (
                <Row
                  label="Discount"
                  value={`−£${(order.discount_amount_pence / 100).toFixed(2)}`}
                />
              )}
              <hr style={{ border: 'none', borderTop: '1px solid var(--a-line)' }} />
              <Row
                label="Subtotal"
                value={`£${(subTotalPence / 100).toFixed(2)}`}
              />
              <Row
                label="Total paid"
                value={`£${(order.total_paid_pence / 100).toFixed(2)}`}
                strong
              />
              {order.late_fee_pence > 0 && (
                <Row
                  label="Late fee charged"
                  value={`£${(order.late_fee_pence / 100).toFixed(2)}`}
                />
              )}
              {order.used_fit_return_exemption && (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 11,
                    color: 'var(--a-ink-soft)'
                  }}
                >
                  Fit-return delivery exemption was used on this order.
                </div>
              )}
              {order.stripe_payment_intent_id && (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 11,
                    color: 'var(--a-ink-soft)',
                    wordBreak: 'break-all'
                  }}
                >
                  Stripe: {order.stripe_payment_intent_id}
                </div>
              )}
            </div>
          </div>

          {/* STATUS EDITOR */}
          <form onSubmit={onSave} className="a-card">
            <div className="a-card-head">
              <h2>Update order</h2>
            </div>
            <div className="a-card-body">
              <div className="a-field">
                <label>Status</label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as OrderStatus)}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="a-field">
                <label>Tracking number</label>
                <input
                  type="text"
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  placeholder="e.g. RZ123456789GB"
                />
                <div className="help">
                  Sent to the customer in the &ldquo;Shipped&rdquo; email when
                  status flips to shipped.
                </div>
              </div>
              <div className="a-field">
                <label>Admin notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Internal only — not shown to the customer."
                />
              </div>
            </div>
            <div className="a-form-foot">
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
                style={{ width: '100%' }}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

function QuickActions({
  order,
  user,
  onUpdated
}: {
  order: Order & { order_items: OrderItem[] };
  user: Profile | null;
  onUpdated: () => void;
}) {
  const [working, setWorking] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const isRental = (order.order_items?.length ?? 0) > 0;
  const idApproved = user?.id_status === 'approved';
  const now = Date.now();
  const overdue =
    order.return_due_date &&
    ['shipped', 'delivered'].includes(order.status) &&
    new Date(order.return_due_date).getTime() < now;

  async function transition(action: string, body: Record<string, unknown>) {
    setWorking(action);
    setMsg(null);
    try {
      await apiFetch(`/api/admin/orders/${order.id}`, {
        method: 'PATCH',
        body: JSON.stringify(body)
      });
      onUpdated();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setWorking(null);
    }
  }

  async function requestId() {
    if (!user) return;
    setWorking('id');
    setMsg(null);
    try {
      await apiFetch(`/api/admin/users/${user.id}/request-id`, {
        method: 'POST'
      });
      setMsg('ID request email sent.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Email failed');
    } finally {
      setWorking(null);
    }
  }

  // What's the natural next step for this order?
  const steps: { label: string; key: string; onClick: () => void; primary?: boolean }[] = [];

  if (isRental && !idApproved) {
    steps.push({
      label: working === 'id' ? 'Sending…' : 'Email customer to upload ID',
      key: 'id',
      onClick: () => void requestId(),
      primary: true
    });
  }

  if (isRental && idApproved && ['confirmed', 'preparing'].includes(order.status)) {
    steps.push({
      label: working === 'ship' ? 'Marking…' : 'Mark as shipped',
      key: 'ship',
      onClick: () => void transition('ship', { status: 'shipped' }),
      primary: true
    });
  }
  if (isRental && order.status === 'shipped') {
    steps.push({
      label: working === 'deliver' ? 'Marking…' : 'Mark as delivered (start 30 days)',
      key: 'deliver',
      onClick: () => void transition('deliver', { status: 'delivered' }),
      primary: true
    });
  }
  // Customer has asked for a return label — admin sends it (and the email
  // goes out automatically when the status flips).
  if (isRental && ['returning', 'delivered', 'shipped'].includes(order.status)) {
    steps.push({
      label: working === 'label' ? 'Marking…' : 'Mark return label sent',
      key: 'label',
      onClick: () =>
        void transition('label', { status: 'return_label_sent' }),
      primary: order.status === 'returning'
    });
  }
  if (
    isRental &&
    [
      'delivered',
      'returning',
      'return_label_sent',
      'shipped'
    ].includes(order.status)
  ) {
    steps.push({
      label: working === 'returned' ? 'Marking…' : 'Mark as returned',
      key: 'returned',
      onClick: () => void transition('returned', { status: 'returned' }),
      primary:
        order.status === 'return_label_sent' ||
        order.status === 'returning' ||
        !!overdue
    });
  }

  if (steps.length === 0 && !msg) return null;

  return (
    <div
      className="a-card"
      style={{
        marginBottom: 20,
        borderLeft: overdue ? '4px solid var(--a-danger, #b3261e)' : undefined
      }}
    >
      <div
        className="a-card-body"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center'
        }}
      >
        {overdue && (
          <div
            style={{
              flex: '1 1 100%',
              color: 'var(--a-danger, #b3261e)',
              fontWeight: 700,
              fontSize: 13,
              marginBottom: 4
            }}
          >
            ⚠ This order is past its 30-day return.
          </div>
        )}
        {!isRental && order.status === 'confirmed' && (
          <div style={{ fontSize: 13, color: 'var(--a-ink-soft)' }}>
            Credit bundle purchase — no shipping action required.
          </div>
        )}
        {steps.map((s) => (
          <button
            key={s.key}
            type="button"
            className={s.primary ? 'btn-primary' : 'btn-secondary'}
            disabled={working !== null}
            onClick={s.onClick}
          >
            {s.label}
          </button>
        ))}
        {msg && (
          <div
            style={{
              flex: '1 1 100%',
              fontSize: 12,
              color: msg.includes('sent') ? 'var(--a-good, #1f7a3d)' : 'var(--a-danger)',
              marginTop: 6
            }}
          >
            {msg}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  strong
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontWeight: strong ? 700 : 400,
        fontSize: strong ? 14 : 13
      }}
    >
      <span style={{ color: 'var(--a-ink-soft)' }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}
